import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { DialogData, SheetInfo, SheetRangeSelectionResult } from '@model/sheet-range-selection.model';
import { ExcelService } from '@service/excel.service';
import { LoggerService } from '@service/logger.service';

@Component({
  selector: 'sheet-range-selection',
  templateUrl: './sheet-range-selection.component.html',
  styleUrls: ['./sheet-range-selection.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatSelect,
    MatOption,
    MatButton,
    MatDialogContent,
    MatDialogActions,
    MatInput,
  ],
})
export class SheetRangeSelectionComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<SheetRangeSelectionComponent, SheetRangeSelectionResult>);
  private excelService = inject(ExcelService);
  private logger = inject(LoggerService);
  private fb = inject(FormBuilder);

  @Inject(MAT_DIALOG_DATA) data: DialogData = {};

  form = this.fb.group({
    sheetId: ['', Validators.required],
    columnLetter: ['A', Validators.required],
    startRow: [1, [Validators.required, Validators.min(1)]],
  });

  sheets: SheetInfo[] = [];
  selectedSheetColumns: string[] = [];
  selectedSheetName: string = '';
  previewData: string[] = [];
  isLoading = false;

  ngOnInit(): void {
    this.loadAvailableSheets();
  }

  private loadAvailableSheets(): void {
    this.isLoading = true;

    this.excelService.listSheetsWithColumns().subscribe({
      next: (sheets) => {
        this.sheets = sheets;
        this.logger.info(`Found ${sheets.length} sheets in workbook`);

        const defaultSheet = sheets.find((s) => s.id === this.data.defaultSheetId) || sheets.find((s) => s.name === 'Sheet2') || sheets[0];
        if (defaultSheet) {
          this.form.patchValue({ sheetId: defaultSheet.id });
          this.onSheetChange(defaultSheet.id);
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.logger.error('Failed to load sheets', err);
        this.isLoading = false;
      },
    });
  }

  onSheetChange(sheetId: string): void {
    const sheet = this.sheets.find((s) => s.id === sheetId);
    if (sheet) {
      this.selectedSheetName = sheet.name;
      this.selectedSheetColumns = sheet.columnLetters;
      this.form.patchValue({ columnLetter: 'A' });
      this.loadPreview(sheetId, 'A');
    }
  }

  private loadPreview(sheetId: string, columnLetter: string): void {
    const startRow = this.form.get('startRow')?.value ?? 1;
    this.isLoading = true;

    this.excelService.readColumnRange(sheetId, columnLetter, startRow, 10).subscribe({
      next: (data) => {
        this.previewData = data.filter((item) => item && item.toString().trim().length > 0);
        this.logger.info(`Preview: ${this.previewData.length} items from ${columnLetter}${startRow}`);
        this.isLoading = false;
      },
      error: (err) => {
        this.logger.error('Failed to load preview', err);
        this.previewData = [];
        this.isLoading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.form.invalid) {
      return;
    }

    const selectedSheet = this.sheets.find((s) => s.id === this.form.value.sheetId);
    if (!selectedSheet) {
      return;
    }

    this.dialogRef.close({
      sheetId: this.form.value.sheetId!,
      sheetName: selectedSheet.name,
      columnLetter: this.form.value.columnLetter!,
      startRow: this.form.value.startRow!,
    });
  }
}
